import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import QuickContactModal from './QuickContactModal';
import { DISTRICTS_KERALA } from '../types/schema';

const ITEMS_PER_PAGE = 50;

// Tooltip wrapper
const Tip = ({ label, children }) => (
  <div className="relative group/tip">
    {children}
    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50 shadow-lg">
      {label}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

// Action dropdown menu for table rows
const ActionMenu = ({ donor, isAvailable, onToggleStatus, onRecordDonation, onPromote, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <Tip label="More actions">
        <button onClick={() => setOpen(o => !o)}
          className={`p-1.5 rounded-lg transition-colors ${open ? 'bg-gray-200 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}>
          ⚙️
        </button>
      </Tip>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-1 text-sm">
          <button onClick={() => { onToggleStatus(donor); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 text-left text-gray-700">
            <span>{isAvailable ? '⏸️' : '▶️'}</span>
            {isAvailable ? 'Deactivate Donor' : 'Activate Donor'}
          </button>
          <button onClick={() => { onRecordDonation?.(donor); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 text-left text-gray-700">
            <span>🩸</span> Record Donation
          </button>
          <button onClick={() => { onPromote(donor); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 text-left text-purple-700">
            <span>⭐</span> Promote to Admin
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button onClick={() => { onDelete(donor); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-red-50 text-left text-red-600">
            <span>🗑️</span> Delete Donor
          </button>
        </div>
      )}
    </div>
  );
};

const DonorList = ({ 
  donors,
  admins,
  isLoading, 
  selectedDonors, 
  onToggleSelection, 
  onContact, 
  onToggleStatus, 
  onPromote, 
  onDelete,
  onLogContact,
  onRecordDonation,
  onEdit,
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  request = null
}) => {
  const [viewMode, setViewMode] = useState('compact'); // 'compact' | 'card'
  const [page, setPage] = useState(1);
  const [contactModal, setContactModal] = useState({ open: false, donor: null });
  const listRef = useRef(null);

  // Build a Set of donor IDs that have been promoted to admin
  const promotedDonorIds = useMemo(() => {
    const s = new Set();
    (admins || []).forEach(a => { if (a.original_donor_id) s.add(a.original_donor_id); });
    return s;
  }, [admins]);

  // Filter and sort donors
  const filteredDonors = donors?.filter(d => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.blood_group?.toLowerCase().includes(q) ||
      d.district?.toLowerCase().includes(q) ||
      d.phone_number?.includes(q) ||
      d.location?.toLowerCase().includes(q)
    );
  }).filter(d => {
    if (filters.bloodGroup && d.blood_group !== filters.bloodGroup) return false;
    if (filters.district && d.district !== filters.district) return false;
    if (filters.isAvailable !== undefined && d.is_available !== filters.isAvailable && d.isAvailable !== filters.isAvailable) return false;
    if (filters.isAdmin === true && !promotedDonorIds.has(d.id)) return false;
    return true;
  }) || [];

  // Pagination
  const totalPages = Math.ceil(filteredDonors.length / ITEMS_PER_PAGE);
  const paginatedDonors = filteredDonors.slice(0, page * ITEMS_PER_PAGE);
  const hasMore = paginatedDonors.length < filteredDonors.length;

  // Handle select all
  const handleSelectAll = () => {
    if (selectedDonors.length === filteredDonors.length) {
      // Deselect all
      filteredDonors.forEach(d => onToggleSelection(d.id));
    } else {
      // Select all filtered
      filteredDonors.forEach(d => {
        if (!selectedDonors.includes(d.id)) {
          onToggleSelection(d.id);
        }
      });
    }
  };

  // Virtual scroll for compact view + infinite scroll trigger
  const handleScroll = useCallback(() => {
    if (!listRef.current || viewMode !== 'compact') return;
    
    const { scrollTop, clientHeight, scrollHeight } = listRef.current;
    
    // Load more when near bottom (within 300px)
    if (scrollTop + clientHeight >= scrollHeight - 300 && hasMore && !isLoading) {
      setPage(p => p + 1);
    }
  }, [filteredDonors.length, viewMode, hasMore, isLoading, paginatedDonors.length]);

  // Infinite scroll observer - works in both compact and card view
  const observerRef = useRef(null);
  const lastElementRef = useCallback((node) => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(p => p + 1);
      }
    }, { threshold: 0.1 });
    
    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filters.bloodGroup, filters.district, filters.isAvailable]);

  const handleQuickContact = (donor, method) => {
    if (method === 'whatsapp' || method === 'sms' || method === 'email') {
      setContactModal({ open: true, donor, request });
    } else if (method === 'call') {
      window.location.href = `tel:${donor.phone_number}`;
      onLogContact?.({ donorId: donor.id, method: 'call', status: 'initiated' });
    }
  };

  // Mobile Card Component - Optimized for small screens
  const MobileCard = ({ donor }) => {
    const isAvailable = donor.is_available !== false && donor.isAvailable !== false;
    const isSelected = selectedDonors.includes(donor.id);
    const [showMenu, setShowMenu] = useState(false);
    
    return (
      <div className={`bg-white rounded-xl border p-3 relative ${isSelected ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200'}`}>
        {/* Header Row */}
        <div className="flex items-center gap-2 mb-3">
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => onToggleSelection(donor.id)}
            className="w-5 h-5 rounded border-gray-300 text-red-600"
          />
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-red-700">{donor.blood_group}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-bold text-gray-900 truncate">{donor.name}</p>
              {promotedDonorIds.has(donor.id) && (
                <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold leading-none">⭐ Admin</span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{donor.location}, {donor.district}</p>
          </div>
          
          {/* Status Badge + Menu Button */}
          <div className="flex items-center gap-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {isAvailable ? '✓' : '✕'}
            </span>
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg active:bg-gray-200"
            >
              ⚙️
            </button>
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="flex items-center gap-2 text-sm mb-3">
          <a href={`tel:${donor.phone_number}`} className="text-blue-600 font-medium">
            {donor.phone_number}
          </a>
        </div>
        
        {/* Quick Actions Grid */}
        <div className="grid grid-cols-4 gap-2">
          <button 
            onClick={async () => {
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              const message = `Hi ${donor.name}! This is an urgent blood donation request for ${donor.blood_group} in ${donor.district}. Can you help? Please reply if you're available. Thank you! 🩸\n\nOCYM Blood Donation Team`;
              
              if (isMobile) {
                const phone = donor.phone_number?.replace(/\D/g, '');
                window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`, '_blank');
              } else {
                await navigator.clipboard.writeText(message);
                toast.success(`Copied! Phone: ${donor.phone_number}`, { duration: 3000 });
              }
              onLogContact?.({ donorId: donor.id, method: 'whatsapp', status: isMobile ? 'sent' : 'copied' });
            }}
            className="flex flex-col items-center p-2 bg-green-50 text-green-700 rounded-lg active:bg-green-100"
          >
            <span className="text-lg">📱</span>
            <span className="text-xs">WhatsApp</span>
          </button>
          <button 
            onClick={() => {
              window.location.href = `tel:${donor.phone_number}`;
              onLogContact?.({ donorId: donor.id, method: 'call', status: 'initiated' });
            }}
            className="flex flex-col items-center p-2 bg-blue-50 text-blue-700 rounded-lg active:bg-blue-100"
          >
            <span className="text-lg">📞</span>
            <span className="text-xs">Call</span>
          </button>
          <button 
            onClick={async () => {
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              const message = `Hi ${donor.name}! Urgent blood donation request for ${donor.blood_group} in ${donor.district}. Can you help? OCYM Team`;
              
              if (isMobile) {
                window.open(`sms:${donor.phone_number}?body=${encodeURIComponent(message)}`, '_blank');
              } else {
                await navigator.clipboard.writeText(message);
                toast.success(`Copied! Phone: ${donor.phone_number}`, { duration: 3000 });
              }
              onLogContact?.({ donorId: donor.id, method: 'sms', status: isMobile ? 'sent' : 'copied' });
            }}
            className="flex flex-col items-center p-2 bg-purple-50 text-purple-700 rounded-lg active:bg-purple-100"
          >
            <span className="text-lg">💬</span>
            <span className="text-xs">SMS</span>
          </button>
          <button 
            onClick={() => {
              const subject = encodeURIComponent(`Blood Donation Request - ${donor.blood_group}`);
              const body = encodeURIComponent(`Dear ${donor.name},\n\nWe have an urgent blood donation request for ${donor.blood_group} in ${donor.district}. Can you help?\n\nThank you,\nOCYM Blood Donation Team`);
              window.open(`mailto:${donor.email}?subject=${subject}&body=${body}`, '_blank');
              onLogContact?.({ donorId: donor.id, method: 'email', status: 'sent' });
            }}
            className="flex flex-col items-center p-2 bg-orange-50 text-orange-700 rounded-lg active:bg-orange-100"
          >
            <span className="text-lg">✉️</span>
            <span className="text-xs">Email</span>
          </button>
        </div>
        
        {/* Management Actions Menu (Dropdown) */}
        {showMenu && (
          <div className="mt-3 pt-3 border-t border-gray-100 animate-slide-up">
            <p className="text-xs text-gray-500 mb-2 font-medium">Management Actions</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button 
                onClick={() => { onToggleStatus(donor); setShowMenu(false); }}
                className={`py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${
                  isAvailable ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                }`}
              >
                {isAvailable ? '⏸️ Deactivate' : '▶️ Activate'}
              </button>
              <button 
                onClick={() => { onRecordDonation?.(donor); setShowMenu(false); }}
                className="py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
              >
                🩸 Record Donation
              </button>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => { onEdit?.(donor); setShowMenu(false); }}
                className="flex-1 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium"
              >
                ✏️ Edit
              </button>
              <button 
                onClick={() => { onDelete(donor); setShowMenu(false); }}
                className="flex-1 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium"
              >
                🗑️ Delete
              </button>
            </div>
            <button 
              onClick={() => { onPromote?.(donor); setShowMenu(false); }}
              className="w-full mt-2 py-2 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
            >
              ⭐ Promote to Admin
            </button>
          </div>
        )}
      </div>
    );
  };

  // Card Component
  const Card = ({ donor, ref }) => {
    const isAvailable = donor.is_available !== false && donor.isAvailable !== false;
    const isSelected = selectedDonors.includes(donor.id);
    
    return (
      <div 
        ref={ref}
        className={`bg-white rounded-xl shadow-sm border transition-all ${isSelected ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-100'} hover:shadow-md`}
      >
        <div className="p-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <input 
              type="checkbox" 
              checked={isSelected}
              onChange={() => onToggleSelection(donor.id)}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <h4 className="font-bold text-gray-900">{donor.name}</h4>
              <p className="text-sm text-gray-500">{donor.location}, {donor.district}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <span className="text-sm font-bold text-red-700">{donor.blood_group}</span>
            </div>
          </div>
        </div>

        <div className="px-4 pb-3 flex gap-2 text-sm">
          <a href={`tel:${donor.phone_number}`} className="text-blue-600 hover:underline">{donor.phone_number}</a>
          {donor.email && <span className="text-gray-400">•</span>}
          {donor.email && <span className="text-gray-600 truncate">{donor.email}</span>}
        </div>

        <div className="border-t border-gray-100 p-3">
          <div className="flex gap-2">
            {[
              { m: 'whatsapp', icon: '📱', color: 'bg-green-50 text-green-700' },
              { m: 'call', icon: '📞', color: 'bg-blue-50 text-blue-700' },
              { m: 'sms', icon: '💬', color: 'bg-purple-50 text-purple-700' },
              { m: 'email', icon: '✉️', color: 'bg-orange-50 text-orange-700' }
            ].map(({ m, icon, color }) => (
              <button
                key={m}
                onClick={() => handleQuickContact(donor, m)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${color} hover:opacity-80 transition-opacity`}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100 px-3 py-2 flex gap-2 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => onToggleStatus(donor)}
            className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium ${
              isAvailable ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
            }`}
          >
            {isAvailable ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onPromote(donor)}
            className="flex-1 py-1.5 px-2 rounded-lg text-xs font-medium bg-purple-100 text-purple-700"
          >
            Admin
          </button>
          <button
            onClick={() => onDelete(donor)}
            className="px-2 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700"
          >
            🗑️
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with search and filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3 mb-4">
        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input
              type="text"
              placeholder="Search donors..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            />
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('compact')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'compact' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
              title="Compact List"
            >
              ☰
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
              title="Card View"
            >
              ⊞
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="relative">
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide items-end pr-10">
            {[
              { key: 'bloodGroup', label: 'Blood Group', options: ['All', ...(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'])] },
              { key: 'district', label: 'District', options: ['All', ...DISTRICTS_KERALA] },
              { key: 'isAvailable', label: 'Status', options: ['All', 'Available', 'Unavailable'] }
            ].map(filter => (
              <div key={filter.key} className="flex flex-col gap-1 flex-shrink-0">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-1">{filter.label}</span>
                <select
                  value={filters[filter.key] || ''}
                  onChange={(e) => onFilterChange(filter.key, e.target.value === 'All' ? undefined : e.target.value)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 bg-white whitespace-nowrap"
                >
                  {filter.options.map(opt => (
                    <option key={opt} value={opt === 'All' ? '' : opt}>{opt}</option>
                  ))}
                </select>
              </div>
            ))}

            {/* Admins-only toggle chip */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-1">Role</span>
              <button
                onClick={() => onFilterChange('isAdmin', filters.isAdmin ? undefined : true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap border transition-colors ${
                  filters.isAdmin
                    ? 'bg-purple-100 border-purple-300 text-purple-800'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                ⭐ Admins only
              </button>
            </div>
            
            {(searchQuery || filters.bloodGroup || filters.district || filters.isAvailable !== undefined || filters.isAdmin) && (
              <button
                onClick={() => {
                  onSearchChange('');
                  onFilterChange('bloodGroup', undefined);
                  onFilterChange('district', undefined);
                  onFilterChange('isAvailable', undefined);
                  onFilterChange('isAdmin', undefined);
                }}
                className="px-3 py-1.5 text-red-600 text-sm font-medium hover:bg-red-50 rounded-lg whitespace-nowrap"
              >
                Clear Filters
              </button>
            )}
          </div>
          {/* Subtle fade-out gradient indicator for horizontal scroll */}
          <div className="pointer-events-none absolute right-0 bottom-0 top-0 w-10 bg-gradient-to-l from-white via-white/80 to-transparent" />
        </div>

        {/* Selection Count Only - No Bulk Actions */}
        {selectedDonors.length > 0 && (
          <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-blue-800 font-medium">
              {selectedDonors.length} donors selected
            </span>
            <button 
              onClick={() => filteredDonors.forEach(d => selectedDonors.includes(d.id) && onToggleSelection(d.id))}
              className="px-3 py-1 text-xs text-blue-700 hover:bg-blue-100 rounded-lg"
            >
              Clear All
            </button>
          </div>
        )}

        {/* Results count & Select All */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-500">Showing {paginatedDonors.length} of {filteredDonors.length} donors</span>
          
          {filteredDonors.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="text-red-600 font-medium hover:text-red-700"
            >
              {selectedDonors.length === filteredDonors.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
      </div>

      {/* Donor List */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin text-4xl mb-2">⏳</div>
            <p className="text-gray-500">Loading donors...</p>
          </div>
        </div>
      ) : filteredDonors.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-gray-500">No donors found</p>
            {(searchQuery || filters.bloodGroup || filters.district) && (
              <button
                onClick={() => {
                  onSearchChange('');
                  onFilterChange('bloodGroup', undefined);
                  onFilterChange('district', undefined);
                  onFilterChange('isAvailable', undefined);
                }}
                className="mt-2 text-red-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      ) : viewMode === 'compact' ? (
        // Compact View - Mobile Cards + Desktop Table
        <div 
          ref={listRef}
          onScroll={handleScroll}
          className="flex-1 overflow-auto min-h-0"
        >
          {/* Mobile Cards - small screens only */}
          <div className="md:hidden space-y-3 p-2">
            {paginatedDonors.map((donor, index) => (
              <div 
                key={donor.id} 
                ref={index === paginatedDonors.length - 1 ? lastElementRef : null}
              >
                <MobileCard donor={donor} />
              </div>
            ))}
          </div>

          {/* Desktop Table - md+ screens */}
          <table className="hidden md:table w-full border-collapse table-fixed">
            <colgroup>
              <col style={{ width: '2rem' }} />
              <col style={{ width: '3rem' }} />
              <col />
              <col style={{ width: '8rem' }} className="hidden lg:table-column" />
              <col style={{ width: '7rem' }} className="hidden xl:table-column" />
              <col style={{ width: '5.5rem' }} />
              <col style={{ width: '13rem' }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b-2 border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2 text-left">BG</th>
                <th className="px-3 py-2 text-left">Name / Location</th>
                <th className="px-3 py-2 text-left hidden lg:table-cell">Phone</th>
                <th className="px-3 py-2 text-left hidden xl:table-cell">Last Donated</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDonors.map((donor, idx) => {
                const isAvailable = donor.is_available !== false && donor.isAvailable !== false;
                const isSelected = selectedDonors.includes(donor.id);
                return (
                  <tr key={donor.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={isSelected} onChange={() => onToggleSelection(donor.id)}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                    </td>
                    {/* Blood Group */}
                    <td className="px-2 py-2">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-red-700">{donor.blood_group}</span>
                      </div>
                    </td>
                    {/* Name / Location — overflow:hidden on td enables truncation */}
                    <td className="px-3 py-2 overflow-hidden">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{donor.name}</p>
                        {promotedDonorIds.has(donor.id) && (
                          <Tip label="Also an Admin">
                            <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold leading-none">⭐ Admin</span>
                          </Tip>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{[donor.location, donor.district].filter(Boolean).join(', ')}</p>
                    </td>
                    {/* Phone */}
                    <td className="px-3 py-2 text-sm text-gray-600 hidden lg:table-cell overflow-hidden">
                      <span className="truncate block">{donor.phone_number}</span>
                    </td>
                    {/* Last Donated */}
                    <td className="px-3 py-2 text-sm text-gray-500 hidden xl:table-cell">
                      {donor.last_donation_date
                        ? new Date(donor.last_donation_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Never'}
                    </td>
                    {/* Status */}
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                        isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{isAvailable ? 'Active' : 'Inactive'}</span>
                    </td>
                    {/* Actions */}
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1 flex-nowrap">
                        <Tip label="WhatsApp"><button onClick={() => handleQuickContact(donor, 'whatsapp')} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg">📱</button></Tip>
                        <Tip label="Call"><button onClick={() => handleQuickContact(donor, 'call')} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">📞</button></Tip>
                        <Tip label="SMS"><button onClick={() => handleQuickContact(donor, 'sms')} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg">💬</button></Tip>
                        <div className="w-px h-5 bg-gray-200 mx-1 flex-shrink-0" />
                        <ActionMenu
                          donor={donor}
                          isAvailable={isAvailable}
                          onToggleStatus={onToggleStatus}
                          onRecordDonation={onRecordDonation}
                          onPromote={onPromote}
                          onDelete={onDelete}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {/* Load more trigger row */}
              {hasMore && (
                <tr ref={lastElementRef}>
                  <td colSpan="7" className="py-4 text-center">
                    <div className="animate-spin inline-block text-2xl">⏳</div>
                    <p className="text-sm text-gray-500 mt-1">Loading more...</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        // Card Grid with Infinite Scroll - Responsive columns
        <div 
          className="flex-1 overflow-auto min-h-0 p-2"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-4">
            {paginatedDonors.map((donor, index) => (
              <MobileCard 
                key={donor.id} 
                donor={donor}
                ref={index === paginatedDonors.length - 1 ? lastElementRef : null}
              />
            ))}
          </div>
          
          {isLoading && (
            <div className="text-center py-4">
              <div className="animate-spin text-2xl">⏳</div>
              <p className="text-sm text-gray-500 mt-2">Loading more...</p>
            </div>
          )}
        </div>
      )}

      {/* Contact Modal */}
      {contactModal.open && (
        <QuickContactModal
          donor={contactModal.donor}
          request={contactModal.request}
          onClose={() => setContactModal({ open: false, donor: null, request: null })}
          onLogContact={onLogContact}
        />
      )}
    </div>
  );
};

export default DonorList;
